import React, { useState } from 'react'
import {
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from '@coreui/react-pro'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useSsoLinkVisibility } from 'src/hooks/useSsoLinkVisibility'
import { userLogout } from 'src/redux/user/userSlice'
import { setAccountLinkingContext } from 'src/utils/sessionStorageUtils'

import { invalidateToken } from '../../../helper/omniauthHelper'

import UserSettingPopup from './UserSettingPopup'
import './PageHeader.scss'
import './SettingsDropdown.scss'

const capitalizeEachWord = (sentence) => {
  return sentence
    ?.toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')
}

export default function SettingsDropdown() {
  const appVer = process.env.REACT_APP_VERSION
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector((state) => state.user?.user)
  const practice = useSelector((state) => state.user?.practice)

  const [userSettingsPopupModel, setUserSettingsPopupModel] = useState(false)

  // Use custom hook to determine SSO link visibility
  const showSsoLinking = useSsoLinkVisibility(practice)

  /**
   * Initiates the SSO account linking process for the current user
   *
   * When a user clicks "Link to SSO" in the settings dropdown, this handler:
   * 1. Validates that the practice has SSO configured
   * 2. Stores the tenant key in sessionStorage to track linking context
   * 3. Navigates to the SSO login page where the user will authenticate with their IdP
   * 4. Upon successful IdP authentication, the callback will link the SSO identity to the user's account
   *
   * The linking context in sessionStorage allows the SSOCallback component to distinguish
   * between a new login flow and an account linking flow.
   *
   * @function handleLinkToSso
   * @returns {void}
   */
  const handleLinkToSso = () => {
    const tenantKey = practice?.sso_tenant_key
    if (!tenantKey) {
      toast.error('SSO is not configured for this practice')
      return
    }

    toast.info('Starting account linking process...')

    // Store linking context in sessionStorage
    setAccountLinkingContext(tenantKey)

    // Navigate to SSO login page with linking context
    navigate(`/login-sso/${tenantKey}`)
  }

  return (
    <>
      <div className="profile align-self-center">
        <CDropdown className="avtar-census">
          <CDropdownToggle color="secondary" className="census-right-header-char-bg">
            <div>
              {capitalizeEachWord(user?.displayname?.charAt(0) || user?.username?.charAt(0))}
            </div>
            <CDropdownMenu>
              <CDropdownItem>
                <div
                  className="fab_item"
                  onClick={() => {
                    navigate('/resetPassword/changePassword')
                  }}
                >
                  <i className="fa-regular fa-user" style={{ fontSize: '1.3em' }}></i>
                  <span className="fab-item-text">Change password</span>
                </div>
              </CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem
                onClick={() => {
                  setUserSettingsPopupModel(true)
                }}
              >
                <div>
                  <i title="User Settings Gear icon" className="fa-regular fa-gear icon"></i>
                  <span className="fab-item-text">User Settings</span>
                </div>
              </CDropdownItem>
              <CDropdownDivider />
              {showSsoLinking && (
                <>
                  <CDropdownItem onClick={handleLinkToSso} aria-label="Link to SSO">
                    <div>
                      <i title="Link SSO Account icon" className="fa-regular fa-link icon"></i>
                      <span className="fab-item-text">Link to SSO</span>
                    </div>
                  </CDropdownItem>
                  <CDropdownDivider />
                </>
              )}
              <CDropdownItem
                onClick={() => {
                  window.open('https://claimocity.com/terms-of-service/', '_blank')
                }}
              >
                <div>
                  <i title="Terms of Service icon" className="fa-regular fa-file-lines icon"></i>
                  <span className="fab-item-text">Terms of Service</span>
                </div>
              </CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem>
                <div
                  className="fab-item"
                  onClick={async () => {
                    await invalidateToken()
                    dispatch(userLogout())
                    navigate('/login')
                  }}
                >
                  <i className="fa-regular fa-user" style={{ fontSize: '1.2em' }}></i>
                  <span>
                    <span className="fab-item-text">Log Out</span>
                  </span>
                </div>
              </CDropdownItem>
              <div className="mt-3 version-cursor">
                <span>Version {appVer}</span>
              </div>
            </CDropdownMenu>
          </CDropdownToggle>
        </CDropdown>
      </div>

      <UserSettingPopup
        visible={userSettingsPopupModel}
        setUserSettingPopupModel={setUserSettingsPopupModel}
      ></UserSettingPopup>
    </>
  )
}
